import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";

const num = (n, d = 2) => Number(n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: d });
const BASE_LABELS = { brut: "brut total", plafond: "base plafonnée", csg: "base CSG" };

export function formatValue(unit, value) {
  if (value == null) return "—";
  if (unit === "%") return `${num(value, 3)} %`;
  if (unit === "€") return `${num(value)} €`;
  if (unit === "€/h") return `${num(value)} €/h`;
  if (unit === "h") return `${num(value)} h`;
  if (unit === "cotisation") return `sal. ${num(value.sal, 3)} % · pat. ${num(value.pat, 3)} % (${BASE_LABELS[value.base] || value.base})`;
  if (unit === "bareme") return value.map((t) => `${t.up_to == null ? "au-delà" : "≤ " + num(t.up_to) + " €"} : ${num(t.rate, 2)} %`).join(" · ");
  return String(value);
}

const NumField = ({ label, value, onChange, testid, step = "0.01" }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}</Label>
    <Input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))} data-testid={testid} className="h-11 font-mono" />
  </div>
);

export function VersionValueEditor({ unit, value, onChange }) {
  if (unit === "cotisation") {
    const v = value || { sal: 0, pat: 0, base: "brut" };
    return (
      <div className="grid sm:grid-cols-3 gap-3" data-testid="bareme-editor-cotisation">
        <NumField label="Taux salarial (%)" value={v.sal} onChange={(x) => onChange({ ...v, sal: x })} testid="bareme-editor-sal" step="0.001" />
        <NumField label="Taux patronal (%)" value={v.pat} onChange={(x) => onChange({ ...v, pat: x })} testid="bareme-editor-pat" step="0.001" />
        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Assiette</Label>
          <Select value={v.base} onValueChange={(b) => onChange({ ...v, base: b })}>
            <SelectTrigger data-testid="bareme-editor-base" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="brut">Brut total</SelectItem>
              <SelectItem value="plafond">Base plafonnée (PASS)</SelectItem>
              <SelectItem value="csg">Base CSG (abattement)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }
  if (unit === "bareme") {
    const rows = Array.isArray(value) ? value : [];
    const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    return (
      <div className="space-y-2" data-testid="bareme-editor-tranches">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Tranches (plafond annuel croissant · taux %) — dernière tranche sans plafond</Label>
        {rows.map((t, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`bareme-tranche-${i}`}>
            <Input type="number" step="1" placeholder="au-delà" value={t.up_to ?? ""} onChange={(e) => set(i, { up_to: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`bareme-tranche-upto-${i}`} className="col-span-6 h-10 font-mono" />
            <Input type="number" step="0.01" value={t.rate ?? ""} onChange={(e) => set(i, { rate: e.target.value === "" ? "" : parseFloat(e.target.value) })} data-testid={`bareme-tranche-rate-${i}`} className="col-span-4 h-10 font-mono" />
            <Button type="button" variant="outline" size="sm" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`bareme-tranche-remove-${i}`} className="col-span-2 h-10 px-2"><X className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { up_to: null, rate: 0 }])} data-testid="bareme-tranche-add" className="h-9"><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une tranche</Button>
      </div>
    );
  }
  return <NumField label={`Nouvelle valeur (${unit})`} value={value} onChange={onChange} testid="bareme-editor-value" step={unit === "%" ? "0.001" : "0.01"} />;
}
